import React, { useContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import { getError } from '../utils';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        shippingPrices: action.payload,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'CREATE_REQUEST':
    case 'UPDATE_REQUEST':
    case 'DELETE_REQUEST':
      return { ...state, loadingAction: true };
    case 'CREATE_SUCCESS':
    case 'UPDATE_SUCCESS':
    case 'DELETE_SUCCESS':
      return { ...state, loadingAction: false };
    case 'ACTION_FAIL':
      return { ...state, loadingAction: false };
    default:
      return state;
  }
};

export default function ShippingPriceListScreen() {
  const [{ loading, error, shippingPrices, loadingAction }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    shippingPrices: [],
  });

  const { state } = useContext(Store);
  const { userInfo } = state;

  // Form state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [city, setCity] = useState('');
  const [price, setPrice] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  // Fetch all shipping prices
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await axios.get('/api/shipping');
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, []);

  // Open modal for new entry
  const handleCreate = () => {
    setEditingId(null);
    setCity('');
    setPrice('');
    setIsDefault(false);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (shippingPrice) => {
    setEditingId(shippingPrice._id);
    setCity(shippingPrice.city);
    setPrice(shippingPrice.price);
    setIsDefault(shippingPrice.isDefault);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setCity('');
    setPrice('');
    setIsDefault(false);
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!city.trim() || !price) {
      toast.error('City and price are required');
      return;
    }

    if (Number(price) < 0) {
      toast.error('Price cannot be negative');
      return;
    }

    try {
      dispatch({ type: editingId ? 'UPDATE_REQUEST' : 'CREATE_REQUEST' });

      const config = {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      };

      if (editingId) {
        // Update
        await axios.put(
          `/api/shipping/${editingId}`,
          { city, price: Number(price), isDefault },
          config
        );
        toast.success('Shipping price updated successfully');
      } else {
        // Create
        await axios.post(
          '/api/shipping',
          { city, price: Number(price), isDefault },
          config
        );
        toast.success('Shipping price created successfully');
      }

      dispatch({ type: editingId ? 'UPDATE_SUCCESS' : 'CREATE_SUCCESS' });

      // Refetch data
      const { data } = await axios.get('/api/shipping');
      dispatch({ type: 'FETCH_SUCCESS', payload: data });

      handleCloseModal();
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'ACTION_FAIL' });
    }
  };

  // Delete handler
  const handleDelete = async (id, cityName, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete the default shipping price. Set another city as default first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the shipping price for ${cityName}?`)) {
      try {
        dispatch({ type: 'DELETE_REQUEST' });
        await axios.delete(`/api/shipping/${id}`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        toast.success('Shipping price deleted successfully');
        dispatch({ type: 'DELETE_SUCCESS' });

        // Refetch data
        const { data } = await axios.get('/api/shipping');
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        toast.error(getError(err));
        dispatch({ type: 'ACTION_FAIL' });
      }
    }
  };

  return (
    <div>
      <Helmet>
        <title>Shipping Prices</title>
      </Helmet>

      <Row className="my-3">
        <Col>
          <h1>Shipping Prices by City</h1>
        </Col>
        <Col className="col text-end">
          <Button type="button" onClick={handleCreate} disabled={loadingAction}>
            Add City
          </Button>
        </Col>
      </Row>

      {loadingAction && <LoadingBox></LoadingBox>}

      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : shippingPrices.length === 0 ? (
        <MessageBox>No shipping prices configured yet</MessageBox>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>CITY</th>
              <th>PRICE</th>
              <th>DEFAULT</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {shippingPrices.map((item) => (
              <tr key={item._id}>
                <td>{item.city}</td>
                <td>{item.price} EGP</td>
                <td>{item.isDefault ? '✓' : '-'}</td>
                <td>
                  <Button
                    type="button"
                    variant="warning"
                    size="sm"
                    onClick={() => handleEdit(item)}
                    disabled={loadingAction}
                    className="me-2"
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(item._id, item.city, item.isDefault)}
                    disabled={loadingAction || item.isDefault}
                    title={item.isDefault ? 'Cannot delete default price' : ''}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Shipping Price' : 'Add New City'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="city">
              <Form.Label>City Name</Form.Label>
              <Form.Control
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g., Alexandria"
                disabled={editingId !== null} // Prevent editing city name
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="price">
              <Form.Label>Shipping Price (EGP)</Form.Label>
              <Form.Control
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g., 90"
                min="0"
                step="0.01"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="isDefault">
              <Form.Check
                type="checkbox"
                label="Set as default (fallback)"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" disabled={loadingAction}>
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button variant="secondary" onClick={handleCloseModal} disabled={loadingAction}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
